<script setup lang="ts">
import { useRouter } from "vue-router";
import { computed, onMounted, ref } from "vue";
import { PopoverArrow, PopoverContent, PopoverPortal, PopoverRoot, PopoverTrigger } from "reka-ui";

import StringTooltip from "@/components/Tooltip/StringTooltip.vue";

import { quarantineService } from "@/services";
import { $can, regExpEscape, trim } from "@/services/UtilitiesService";

import { useMetadataStore } from "@/stores/metadata-store";
import { useQuarantineStore } from "@/stores/quarantine-store";

import { useRootLayout } from "@/hooks/use-root-layout";
import { useCaseRouteParams } from "@/hooks/use-case-route-params";

import { Upload } from "@/models/Upload";
import { UploadStatus } from "@/models/UploadStatus";

import type { QuarantineUpload } from "@/models/Quarantine";

const FILE_NAME_MAX_LENGTH = 128;

defineEmits<(e: "CancelDoubleClick") => void>();

const $router = useRouter();
const routeParams = useCaseRouteParams();
const { sidebarRef } = useRootLayout();

const path = ref<string>("");
const files = ref<Upload[]>([]);
const filePath = ref<string>("");
const newPaths = ref<string[]>([]);
const inputFileRef = ref<HTMLInputElement | null>(null);

const getHierarchy = computed(() => {
  return [...new Set(useMetadataStore().hierarchy.concat(newPaths.value))].sort((l, r) => {
    const collator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: "base",
    });
    return collator.compare(l, r);
  });
});

const hasFiles = computed(() => files.value.length > 0);

const isPathValid = computed(() => {
  if (path.value.trim().length === 0) {
    return true;
  }

  return /(?!.*\/\s{0,}\/)(?!^\/\s{0,}$)^.*/g.test(path.value);
});

//  TODO dobuleclick and eventbus
// $on('DoubleClick', startEditing);
// $on('TargetChanged', () => cancelEditing(true));
// eventBus.on('EscKeyPressed', () => cancelEditing(false));

onMounted(() => {
  if ($can("upload")) return;
  $router.push({ name: "error", params: { brand: routeParams.value.brand, code: "403" } });
});

async function renameUpload(file: Upload): Promise<void> {
  file.name = file.existsCheck.newFileName || file.existsCheck.suggestedFileName;

  await upload(file);
  await useQuarantineStore().refresh();
}

function anyInStatus(statuses: string | string[]) {
  statuses = Array.isArray(statuses) ? statuses : [statuses];
  const item = files.value.find((file) => file.isInStatus(statuses));

  return item !== undefined;
}

function addPath(): void {
  const pathParts = trim(path.value.replace(/\\/g, "/"), "/")
    .split("/")
    .filter((p) => p !== "/");
  // Hold parts that have been found or not here, this will sort the casing issue
  const acceptedPathParts: string[] = [];
  for (let pathIndex = 0; pathIndex < pathParts.length; pathIndex++) {
    let fullPath: string = acceptedPathParts.concat(pathParts.slice(pathIndex, pathIndex + 1)).join("/");
    // If we are adding to an existing folder
    if (filePath.value) {
      fullPath = `${filePath.value}/${fullPath}`;
    }
    const pathMatch = new RegExp(`^${regExpEscape(fullPath)}$`, "i");

    const existingPath = getHierarchy.value.find((m) => pathMatch.test(m));

    // Add the new path if it doesn't exist and keep it in the accepted parts
    if (existingPath === undefined) {
      newPaths.value.push(fullPath);
      acceptedPathParts.push(fullPath);
    } else {
      acceptedPathParts.push(existingPath);
    }
  }
  path.value = "";
  filePath.value = newPaths.value[newPaths.value.length - 1];
}
async function filesChanged(evt: Event): Promise<void> {
  // We need to disallow Directories for now, so check the webkitEntries to find the files that aren't directories
  const targetEl = evt.target as HTMLInputElement;
  if (!targetEl?.files?.length) {
    return;
  }

  const f =
    targetEl.webkitEntries.length === 0
      ? ([...targetEl.files] as ReadonlyArray<File & { isDirectory?: false }>)
      : targetEl.webkitEntries;

  f.forEach((item, index) => {
    const file = targetEl.files?.item(index);
    if (file == null) return;

    const u = new Upload(routeParams.value.brand, routeParams.value.casename, filePath.value, file.name, file);

    if (item.isDirectory) {
      u.status = UploadStatus.CanNotUploadFile;
      u.reason = "Folders can not be uploaded.";
    } else if (item.name.length > FILE_NAME_MAX_LENGTH) {
      u.status = UploadStatus.CanNotUploadFile;
      u.reason = `Filename is greater than ${FILE_NAME_MAX_LENGTH} chars`;
    }

    files.value.push(u);
  });

  const validFiles = files.value.filter((file) => file.isInStatus("Waiting"));

  await Promise.all(validFiles.map((vf) => checkFileExists(vf, null)));

  await useQuarantineStore().refresh();
}

async function checkFileExists(file: Upload, fileName: string | null): Promise<void> {
  file.status = UploadStatus.Processing;

  try {
    const response = await quarantineService.exists(
      routeParams.value.client,
      routeParams.value.brand,
      routeParams.value.casename,
      { fileName: fileName || file.fullFileName },
    );
    // we have an existing file at this name, we need to allow the user to choose to overwrite
    file.existsCheck.fileReference = response.fileReference;
    file.existsCheck.suggestedFileName = response.suggestedFileName;
    file.existsCheck.newFileName = file.existsCheck.suggestedFileName;
    file.existsCheck.status = UploadStatus.Success;
    file.status = UploadStatus.Waiting;
  } catch (r) {
    if ((r as Response).status === 204) {
      // Start automatic upload
      file.existsCheck.status = UploadStatus.Success;
      file.status = UploadStatus.Waiting;
      if (!fileName) {
        await upload(file);
      }

      return;
    }

    file.existsCheck.status = UploadStatus.Failed;
    file.status = UploadStatus.Failed;
  }
}

function removeFile(index: number): void {
  files.value.splice(index, 1);
  inputFileRef.value!.value = "";
}

async function upload(file: Upload): Promise<void> {
  const fileReference = file.existsCheck.overwrite ? file.existsCheck.fileReference : file.fileReference;
  const item: QuarantineUpload = {
    fileName: file.filename,
    fileReference,
    edxFileName: file.fullFileName,
    source: "User Upload",
  };

  file.status = UploadStatus.Processing;
  try {
    await quarantineService.save(
      routeParams.value.client,
      routeParams.value.brand,
      routeParams.value.casename,
      item,
      file.filename,
      file.file!,
    );
    file.status = UploadStatus.Success;
  } catch (error) {
    file.status = UploadStatus.Failed;
  }
}

function clear(): void {
  files.value = [];
  inputFileRef.value!.value = "";
}

function backToCase(): void {
  $router.push({ name: "case", params: { brand: routeParams.value.brand, casename: routeParams.value.casename } });
}
</script>
<template>
  <Teleport :to="sidebarRef">
    <StringTooltip side="right" item="Back to Case" #="{ item }">
      <button
        type="button"
        class="tw-btn-md-white tw-grid tw-place-items-center tw-rounded-md tw-bg-gray-300 tw-px-3 tw-py-1.5 tw-text-gray-800"
        :aria-label="item"
        @click.prevent="backToCase"
      >
        <i class="fas fa-arrow-left"></i>
      </button>
    </StringTooltip>
  </Teleport>
  <div class="tw-relative tw-h-full tw-overflow-auto">
    <div class="tw-mx-8 tw-my-4 tw-bg-gray-100 tw-p-8">
      <div
        class="tw-mb-4 tw-flex tw-flex-wrap tw-items-center tw-gap-2"
        :class="{ 'tw-is-input-invalid': !isPathValid && path.length > 0 }"
      >
        <div class="tw-flex tw-min-w-0 tw-flex-wrap tw-items-center">
          <label for="destination" class="tw-mr-3">Destination folder</label>
          <select
            id="destination"
            v-model="filePath"
            :disabled="anyInStatus('Processing')"
            class="tw-min-w-20 tw-rounded-md tw-border tw-border-gray-500 tw-px-2 tw-py-1"
          >
            <option value="">/</option>
            <option v-for="leaf in getHierarchy" :key="leaf" :value="leaf">/{{ leaf }}</option>
          </select>
        </div>
        <div class="tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">
          <label for="newPath">Add new path</label>
          <input
            id="newPath"
            v-model.trim="path"
            type="text"
            class="tw-mr-1 tw-min-w-0 tw-rounded-md tw-border tw-border-gray-500 tw-p-1"
            :class="{
              'tw-is-input-invalid': !isPathValid && path.length > 0,
            }"
            pattern="^[^\/]+$"
          />
          <button
            type="button"
            title="Add path"
            :disabled="path.length === 0 || !isPathValid"
            class="tw-btn-md-white tw-rounded-md tw-border tw-border-gray-500"
            @click.prevent="addPath"
          >
            <i class="fas fa-plus"></i>
          </button>
        </div>
        <button
          v-if="hasFiles"
          type="button"
          :disabled="anyInStatus('Processing')"
          class="tw-btn-lg-gray tw-flex tw-min-w-max tw-items-center tw-justify-start tw-gap-2 disabled:tw-cursor-not-allowed disabled:tw-bg-gray-300 disabled:tw-text-gray-600"
          @click.prevent="clear"
        >
          Clear files
        </button>
      </div>
      <div v-show="!hasFiles" class="tw-relative tw-rounded-md tw-bg-blue-200">
        <input
          ref="inputFileRef"
          type="file"
          name="uploadFile"
          multiple="true"
          class="input-file"
          @change.prevent="filesChanged"
        />
        <h3 class="tw-my-0 tw-py-10 tw-text-center tw-text-blue-900">
          <span>
            Drag your file here to upload
            <br />or click to browse
          </span>
        </h3>
      </div>
      <div v-if="hasFiles" class="tw-flex tw-flex-wrap tw-gap-5">
        <div
          v-for="(file, index) in files"
          :key="`${file.name}-${index}`"
          :aria-label="`File ${index + 1}'s Status.`"
          class="tw-min-w-[18.75rem] tw-max-w-[18.75rem] tw-space-y-3 tw-overflow-hidden tw-rounded-md tw-border tw-border-gray-500 tw-p-2.5"
        >
          <h5 class="tw-line-clamp-2 tw-text-base tw-font-semibold">{{ file.name }}</h5>
          <div class="tw-grid tw-grid-cols-[minmax(min-content,0.5fr)_minmax(max-content,1fr)] tw-gap-x-4 tw-gap-y-2">
            <div class="tw-col-span-full tw-grid tw-grid-cols-subgrid tw-grid-rows-1">
              <span class="tw-font-bold tw-underline tw-underline-offset-2">Status:</span>
              <p class="tw-text-balance tw-text-right">{{ file.status }}</p>
            </div>
            <div
              v-if="file.isInStatus('CanNotUploadFile')"
              class="tw-col-span-full tw-grid tw-grid-cols-subgrid tw-grid-rows-1"
            >
              <span class="tw-font-bold tw-underline tw-underline-offset-2">Reason:</span>
              <p class="tw-text-balance tw-text-right">{{ file.reason }}</p>
            </div>
            <div
              v-if="file.existsCheck.exists && file.isInStatus('Waiting')"
              class="tw-col-span-full tw-grid tw-grid-cols-subgrid"
            >
              <span class="tw-font-bold tw-underline tw-underline-offset-2">Duplicate:</span>
              <div class="tw-flex tw-flex-col tw-gap-2">
                <p class="tw-text-balance tw-text-right">A file already exists with this name!</p>
                <PopoverRoot>
                  <PopoverTrigger type="button" class="tw-btn-md-gray tw-min-w-20">Rename</PopoverTrigger>
                  <PopoverPortal>
                    <PopoverContent :as-child="true">
                      <form
                        class="tw-grid tw-max-h-[calc(var(--reka-popover-content-available-height)-1rem)] tw-max-w-sm tw-grid-cols-[max-content_minmax(0,1fr)] tw-gap-x-4 tw-gap-y-3 tw-overflow-y-auto tw-rounded-lg tw-border tw-border-gray-500 tw-bg-white tw-p-2 tw-shadow"
                        @submit.prevent="() => renameUpload(file)"
                      >
                        <div class="tw-col-span-full tw-grid tw-grid-cols-subgrid tw-grid-rows-1 tw-items-center">
                          <span class="tw-font-bold">Original:</span>
                          <p class="tw-text-balance tw-text-right">{{ file.name }}</p>
                        </div>
                        <div class="tw-col-span-full tw-grid tw-grid-cols-subgrid tw-grid-rows-1 tw-items-center">
                          <span class="tw-font-bold">Suggested:</span>
                          <p class="tw-text-balance tw-text-right">{{ file.existsCheck.suggestedFileName }}</p>
                        </div>
                        <div class="tw-col-span-full tw-grid tw-grid-cols-subgrid tw-grid-rows-1 tw-items-center">
                          <label for="newFileName" class="tw-font-bold">New:</label>
                          <input
                            id="newFileName"
                            v-model="file.existsCheck.newFileName"
                            type="text"
                            :maxlength="FILE_NAME_MAX_LENGTH"
                            class="tw-w-full tw-rounded-md tw-border tw-border-gray-500 tw-bg-white tw-p-2"
                          />
                        </div>
                        <div class="tw-col-span-full tw-flex tw-items-center tw-justify-end">
                          <button type="submit" class="tw-btn-md-green invert tw-min-w-20">Rename</button>
                        </div>
                        <PopoverArrow class="tw-fill-white" />
                      </form>
                    </PopoverContent>
                  </PopoverPortal>
                </PopoverRoot>
                <button
                  v-if="file.existsCheck.isInStatus('Success')"
                  type="button"
                  class="tw-btn-md-gray tw-min-w-20"
                  @click.prevent="
                    () => {
                      file.existsCheck.overwrite = true;
                      upload(file);
                    }
                  "
                >
                  Overwrite
                </button>
                <button type="button" class="tw-btn-md-gray tw-min-w-20" @click.prevent="removeFile(index)">
                  Cancel File
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped type="text/css">
.input-file {
  opacity: 0; /* invisible but it's there! */
  width: 100%;
  height: 100%;
  position: absolute;
  cursor: pointer;
}
.filename {
  word-break: break-all;
  width: 60%;
}
</style>
